import React, { useState, useEffect, useRef } from 'react';
import { v1 as uuidv1 } from 'uuid';
import classNames from 'classnames';
import { throttle } from 'lodash';
import { Link } from 'gatsby';
import { motion } from 'framer-motion';
import Logo from './Logo';
import LinkTransition from '../LinkTransition';
import { useWindowDimensions, registerListener, removeListener } from '../../utilities/general';
import * as styles from './mainNav.module.scss';

/* eslint-disable no-undef */
/* eslint-disable jsx-a11y/no-static-element-interactions */

const navItems = [
  {
    to: '/work',
    label: 'Work',
  },
  {
    to: '/services',
    label: 'Services',
  },
  {
    to: '/industries',
    label: 'Clients',
  },
  {
    to: '/about',
    label: 'About',
    submenu:
      [
        {
          title: 'Community Impact',
          to: '/community-impact'
        },
        {
          title: 'Blog',
          to: '/blog'
        }
      ],
  },
  {
    to: '/careers',
    label: 'Careers',
  },
  {
    to: '/contact',
    label: 'Contact',
  },
];

const asideNavItems = [
  {
    to: '/work',
    label: 'Work',
  },
  {
    to: '/services',
    label: 'Services',
  },
  {
    to: '/industries',
    label: 'Clients',
  },
  {
    to: '/about',
    label: 'About',
  },
  {
    to: '/blog',
    label: 'Blog',
  },
  {
    to: '/careers',
    label: 'Careers',
  },
  {
    to: '/contact',
    label: 'Contact',
  },
];

const asideSmallNavItems = [
  {
    to: '/privacy',
    label: 'Privacy',
  },
  {
    to: '/support',
    label: 'Support',
  },
  {
    to: '/careers',
    label: 'Careers',
  },
  {
    to: '/community-impact',
    label: 'Community Impact',
  },
  {
    to: '/blog/the-highnoon-story',
    label: 'Our story',
  },
  {
    to: 'https://az5c.com/',
    label: 'AZ5C',
    external: true,
  },
  {
    to: 'https://edu.lucidagency.com/',
    label: 'Lucid EDU',
    external: true,
  },
];

const MainNav = ({ variant }) => {
  const { width } = useWindowDimensions();

  const [atTop, setAtTop] = useState(true);
  const [showToggle, setShowToggle] = useState(false);
  const [showAside, setShowAside] = useState(false);

  useEffect(() => {
    const ceiling = 0;
    let previousOffset = 0;

    if (typeof window !== 'undefined') {
      previousOffset = window.pageYOffset;
    }

    if (previousOffset <= ceiling) {
      setAtTop(true);
    }

    const onScroll = throttle(() => {
      if (window.pageYOffset <= ceiling) {
        setAtTop(true);
        setShowToggle(false);
      } else if (window.pageYOffset > previousOffset) { // If scrolling down
        setAtTop(false);
        setShowToggle(true);
      } else if (window.pageYOffset < previousOffset) { // If scrolling up
        setShowToggle(true);
      }

      previousOffset = window.pageYOffset;
    }, 250);

    if (typeof window !== 'undefined') {
      registerListener(window, 'scroll', onScroll);
    }

    return () => {
      if (typeof window !== 'undefined') {
        removeListener(window, 'scroll', onScroll);
      }
    };
  }, []);

  const firstTab = useRef(null);
  const lastTab = useRef(null);

  useEffect(() => {
    const onKeyDown = e => {
      if (!showAside) {
        return;
      }

      // If is first tab element and shift/tab is pressed
      if (e.target === firstTab.current && e.shiftKey && e.keyCode === 9) {
        e.preventDefault();
        lastTab.current.focus();
      }

      // If is last tab element and tab is pressed
      if (e.target === lastTab.current && !e.shiftKey && e.keyCode === 9) {
        e.preventDefault();
        firstTab.current.focus();
      }
    };

    if (typeof document !== 'undefined') {
      registerListener(document.documentElement, 'keydown', onKeyDown);
    }

    return () => {
      if (typeof document !== 'undefined') {
        removeListener(document.documentElement, 'keydown', onKeyDown);
      }
    };
  }, [showAside, firstTab, lastTab]);

  return (
    <>
      <header className={classNames(styles.header, styles[variant])}>
        <Link className={styles.logo} to="/" innerRef={firstTab}>
          <Logo variant={showAside ? 'light' : variant} isSmall={!atTop || showAside} />
        </Link>

        <motion.nav
          className={classNames(styles.nav)}
          initial="visible"
          animate={showToggle || showAside || width <= 768 ? 'hidden' : 'visible'}
          variants={{
            visible: {
              opacity: 1,
              display: '',
            },
            hidden: {
              opacity: 0,
              transitionEnd: {
                display: 'none',
              }
            },
          }}
        >
          <ul>
            {navItems.map((item) => (
              <li key={uuidv1()}>
                {item.external
                  ? (
                    <a
                      href={item.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.item}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <LinkTransition
                      to={item.to}
                      className={styles.item}
                      activeClassName={styles.itemActive}
                    >
                      {item.label}
                    </LinkTransition>
                  )}
                {item.submenu
                  ? (
                    <ul className={styles.submenuList}>
                      {item.submenu.map((single) => (
                        <li key={uuidv1()}>
                          <LinkTransition
                            to={single.to}
                            className={styles.submenu}
                            activeClassName={styles.itemActive}
                          >
                            {single.title}
                          </LinkTransition>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <>
                    </>
                  )}
              </li>
            ))}
          </ul>
        </motion.nav>

        <button
          type="button"
          className={classNames(styles.toggle, {
            [styles.shown]: showToggle || showAside,
            [styles.open]: showAside,
          })}
          onClick={() => setShowAside(!showAside)}
        >
          {showAside ? 'Close' : 'Menu'}
        </button>
      </header>

      <motion.aside
        className={styles.aside}
        initial="hidden"
        animate={showAside ? 'visible' : 'hidden'}
        variants={{
          visible: {
            opacity: 1,
            display: '',
          },
          hidden: {
            opacity: 0,
            transitionEnd: {
              display: 'none',
            },
          },
        }}
      >
        <nav className={styles.nav}>
          <ul>
            {asideNavItems.map((item) => (
              <li key={uuidv1()}>
                {item.external
                  ? (
                    <a
                      href={item.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.item}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.to}
                      className={styles.item}
                      activeClassName={styles.itemActive}
                    >
                      {item.label}
                    </Link>
                  )}
              </li>
            ))}
          </ul>
        </nav>

        <ul className={styles.smallNav}>
          {asideSmallNavItems.map((item, index) => (
            <li key={uuidv1()}>
              {item.external
                ? (
                  <a
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.item}
                    ref={index + 1 === asideSmallNavItems.length ? lastTab : null}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    to={item.to}
                    className={styles.item}
                    activeClassName={styles.itemActive}
                    ref={index + 1 === asideSmallNavItems.length ? lastTab : null}
                  >
                    {item.label}
                  </Link>
                )}
            </li>
          ))}
        </ul>
      </motion.aside>
    </>
  );
};

export default MainNav;
